/**
 * A free-trial gym says so on every screen, with the days left (ported from Legal CRM's
 * DemoBanner). Without it a gym owner cannot tell sample data from their own, and finds
 * out the trial ended only when sign-in refuses them. The owner removes the sample data
 * from here. Renders nothing for a normal gym, or while the trial is unknown.
 */
import { describeError } from '@gymcoach/shared';
import { Button, Modal, useToast } from '@gymcoach/ui';
import { useState } from 'react';

import { useMe } from '@/auth/useMe';

import { trialCopy } from './copy';
import { useMyTrial, useRemoveSampleData } from './useTrial';

const b = trialCopy.banner;
const r = trialCopy.remove;

export function TrialBanner() {
  const { role } = useMe();
  const trial = useMyTrial();
  const remove = useRemoveSampleData();
  const { toast } = useToast();
  const [confirming, setConfirming] = useState(false);

  const t = trial.data;
  if (!t?.is_trial || !t.ends_at) return null;
  const ends = new Date(t.ends_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', timeZone: 'Asia/Karachi' });
  const canRemove = role === 'owner' && t.has_sample_data;

  return (
    <div
      role="note"
      aria-label={b.label}
      className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 bg-hero px-4 py-2 text-center text-sm text-hero-ink"
    >
      <span className="rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-ink">{b.label}</span>
      <span>
        <span className="font-semibold">{b.left(t.days_left ?? 0)}</span>{' '}
        <span className="text-hero-muted">{b.until(ends)}</span>
        {t.has_sample_data && <span className="text-hero-muted"> {b.sample}</span>}
      </span>
      <span className="text-hero-muted">
        {b.keep} <span className="select-all font-medium text-hero-ink">{b.contact}</span>
      </span>
      {canRemove && (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="rounded-full border border-hero-ink/30 px-3 py-0.5 text-xs font-semibold text-hero-ink hover:bg-hero-ink/10 focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          {b.remove}
        </button>
      )}
      <Modal
        open={confirming}
        onClose={() => !remove.isPending && setConfirming(false)}
        title={r.title}
        description={r.body}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirming(false)} disabled={remove.isPending}>
              {r.cancel}
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              loadingLabel={r.removing}
              onClick={() =>
                remove.mutate(undefined, {
                  onSuccess: (done) => {
                    setConfirming(false);
                    toast({ title: r.done(done.members ?? 0, done.trainers ?? 0), tone: 'success' });
                  },
                  onError: (error) => toast({ title: describeError(error).message, tone: 'danger' }),
                })
              }
            >
              {r.confirm}
            </Button>
          </>
        }
      >
        <p className="text-sm font-medium text-ink">{r.irreversible}</p>
      </Modal>
    </div>
  );
}
